import React, { useEffect, useRef } from 'react';
import { LEVELS } from './PixelField';
type Props = {
  className?: string;
  /** Same pixel size as the rest of the site's pixel fields */
  cell?: number;
};

// One node per this many cells (higher = sparser network)
const NODE_DENSITY = 110;
// Links reach the nearest neighbours within this distance (cells)
const LINK_REACH = 13;
const LINKS_PER_NODE = 2;
// A new thought starts this often (seconds); at most this many signals travel at once
const SPAWN_EVERY = 1.1;
const MAX_SIGNALS = 5;
// Signal speed (cells per second) and chance an arriving signal fires onward
const SPEED = 9;
const PROPAGATE = 0.72;
// Pixels near UI fade out: padding around each element and the soft edge beyond it (px)
const AVOID_PAD = 24;
const AVOID_SOFT = 64;
// Cursor bends the network like a lens (cells) — nothing follows it
const WARP_RADIUS = 14;
const WARP_AMOUNT = 3.6;
const FOLLOW = 0.07;
const FADE = 0.05;
// Phones: keep the network in the left share of the width, a bit denser so it reads as one cluster
const MOBILE_BREAKPOINT = 768;
const MOBILE_SPAN_X = 0.72;
const MOBILE_DENSITY = 0.6;
type Node = {
  x: number;
  y: number;
  energy: number;
  links: number[];
};
type Edge = {
  a: number;
  b: number;
  cells: [number, number][];
  energy: number;
};
type Signal = {
  edge: number;
  forward: boolean;
  p: number;
};
type Rect = {
  l: number;
  t: number;
  r: number;
  b: number;
};

/**
 * A quiet neural network in the footer's square pixels. Sparse nodes sit in the empty space,
 * joined by barely-there dotted links. Every so often a node "fires": a single bright pixel
 * travels along a link, the next node lights up and may pass the signal on — a chain of
 * thought moving through the network, then settling back to rest.
 * Everything fades out around [data-pixel-avoid] elements so the text stays clean.
 */
export function FooterNetwork({
  className = '',
  cell = 14
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    const host = canvas?.parentElement;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !host || !ctx) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let cols = 0;
    let rows = 0;
    let raf = 0;
    let visible = false;
    let last = performance.now();
    let spawnClock = 0;
    let nodes: Node[] = [];
    let edges: Edge[] = [];
    let signals: Signal[] = [];
    const target = {
      x: 0,
      y: 0
    };
    const smooth = {
      x: 0,
      y: 0
    };
    let hovering = false;
    let strength = 0;

    // Cells along a straight link (Bresenham), excluding both end nodes
    const lineCells = (x0: number, y0: number, x1: number, y1: number) => {
      const out: [number, number][] = [];
      const dx = Math.abs(x1 - x0);
      const dy = -Math.abs(y1 - y0);
      const sx = x0 < x1 ? 1 : -1;
      const sy = y0 < y1 ? 1 : -1;
      let err = dx + dy;
      let x = x0;
      let y = y0;
      while (!(x === x1 && y === y1)) {
        const e2 = 2 * err;
        if (e2 >= dy) {
          err += dy;
          x += sx;
        }
        if (e2 <= dx) {
          err += dx;
          y += sy;
        }
        if (!(x === x1 && y === y1)) out.push([x, y]);
      }
      return out;
    };
    const build = () => {
      // Phones: gather the network in the open middle/left area above the footer's text
      // instead of scattering it everywhere (where most of it would be faded out by the text)
      const isPhone = window.innerWidth < MOBILE_BREAKPOINT;
      let x0 = 0;
      let x1 = cols;
      let y0 = 2;
      let y1 = Math.max(3, rows - 2);
      let density = NODE_DENSITY;
      if (isPhone) {
        const tops = measure().map(r => r.t).filter(t => t > 0);
        const textTop = tops.length ? Math.min(...tops) : rows * cell * 0.5;
        const openRows = Math.floor((textTop - AVOID_PAD) / cell);
        x1 = Math.max(6, Math.round(cols * MOBILE_SPAN_X));
        if (openRows > 10) {
          // Middle band of the open space, leaving a margin top and bottom
          y0 = Math.round(openRows * 0.18);
          y1 = Math.round(openRows * 0.88);
        } else {
          y0 = Math.round(rows * 0.3);
          y1 = Math.round(rows * 0.7);
        }
        density = NODE_DENSITY * MOBILE_DENSITY;
      }
      const area = Math.max(1, (x1 - x0) * (y1 - y0));
      const count = Math.max(12, Math.round(area / density));
      nodes = Array.from({
        length: count
      }, () => ({
        x: x0 + Math.floor(Math.random() * Math.max(1, x1 - x0)),
        y: y0 + Math.floor(Math.random() * Math.max(1, y1 - y0)),
        energy: 0,
        links: []
      }));
      edges = [];
      const seen = new Set<string>();
      nodes.forEach((n, i) => {
        nodes.map((m, j) => ({
          j,
          d: Math.hypot(m.x - n.x, m.y - n.y)
        })).filter(o => o.j !== i && o.d <= LINK_REACH && o.d > 2).sort((p, q) => p.d - q.d).slice(0, LINKS_PER_NODE).forEach(({
          j
        }) => {
          const key = i < j ? `${i}-${j}` : `${j}-${i}`;
          if (seen.has(key)) return;
          seen.add(key);
          const idx = edges.length;
          edges.push({
            a: i,
            b: j,
            cells: lineCells(n.x, n.y, nodes[j].x, nodes[j].y),
            energy: 0
          });
          n.links.push(idx);
          nodes[j].links.push(idx);
        });
      });
      signals = [];
    };
    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const {
        width,
        height
      } = host.getBoundingClientRect();
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const nextCols = Math.ceil(width / cell);
      const nextRows = Math.ceil(height / cell);
      if (nextCols !== cols || nextRows !== rows) {
        cols = nextCols;
        rows = nextRows;
        build();
      }
    };
    const measure = (): Rect[] => {
      const base = canvas.getBoundingClientRect();
      return Array.from(host.querySelectorAll<HTMLElement>('[data-pixel-avoid]')).map(el => {
        const r = el.getBoundingClientRect();
        return {
          l: r.left - base.left,
          t: r.top - base.top,
          r: r.right - base.left,
          b: r.bottom - base.top
        };
      });
    };
    let rects: Rect[] = [];
    const visibility = (x: number, y: number) => {
      const px = x * cell + cell / 2;
      const py = y * cell + cell / 2;
      let v = Math.min(1, y / 5);
      for (const r of rects) {
        const dx = Math.max(r.l - AVOID_PAD - px, 0, px - r.r - AVOID_PAD);
        const dy = Math.max(r.t - AVOID_PAD - py, 0, py - r.b - AVOID_PAD);
        v = Math.min(v, Math.hypot(dx, dy) / AVOID_SOFT);
        if (v <= 0) return 0;
      }
      return Math.min(1, v);
    };
    const plot = (x: number, y: number, level: number) => {
      const m = visibility(x, y);
      if (m <= 0.02 || level <= 0) return;
      let dx = x;
      let dy = y;
      if (strength > 0.001) {
        const ox = x - smooth.x;
        const oy = y - smooth.y;
        const d = Math.hypot(ox, oy);
        if (d < WARP_RADIUS && d > 0.0001) {
          const f = Math.pow(1 - d / WARP_RADIUS, 2) * strength * WARP_AMOUNT;
          dx += ox / d * f;
          dy += oy / d * f;
        }
      }
      ctx.globalAlpha = level * m;
      ctx.fillRect(Math.round(dx) * cell, Math.round(dy) * cell, cell - 1, cell - 1);
    };
    const fire = (nodeIndex: number, fromEdge = -1) => {
      const n = nodes[nodeIndex];
      n.energy = 1;
      if (signals.length >= MAX_SIGNALS) return;
      const options = n.links.filter(e => e !== fromEdge);
      if (!options.length) return;
      const edge = options[Math.floor(Math.random() * options.length)];
      signals.push({
        edge,
        forward: edges[edge].a === nodeIndex,
        p: 0
      });
    };
    const draw = (now: number) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      smooth.x += (target.x - smooth.x) * FOLLOW;
      smooth.y += (target.y - smooth.y) * FOLLOW;
      strength += ((hovering && !reduce ? 1 : 0) - strength) * FADE;
      rects = measure();
      if (!reduce) {
        spawnClock += dt;
        if (spawnClock > SPAWN_EVERY) {
          spawnClock = 0;
          // Start a thought at a node that is actually visible
          for (let tries = 0; tries < 8; tries++) {
            const i = Math.floor(Math.random() * nodes.length);
            if (visibility(nodes[i].x, nodes[i].y) > 0.6) {
              fire(i);
              break;
            }
          }
        }
        signals = signals.filter(s => {
          const e = edges[s.edge];
          s.p += SPEED * dt / Math.max(1, e.cells.length + 1);
          e.energy = 1;
          if (s.p < 1) return true;
          const arrive = s.forward ? e.b : e.a;
          if (Math.random() < PROPAGATE) {
            // Hand off after this signal is removed so the cap doesn't block the relay
            queueMicrotask(() => fire(arrive, s.edge));
          } else {
            nodes[arrive].energy = 1;
          }
          return false;
        });
        for (const n of nodes) n.energy = Math.max(0, n.energy - dt * 0.9);
        for (const e of edges) e.energy = Math.max(0, e.energy - dt * 0.7);
      }
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = '#ffffff';

      // Links: barely-there dotted lines, briefly warmer after a signal passes
      for (const e of edges) {
        e.cells.forEach(([x, y], k) => {
          if (k % 2 && e.energy < 0.05) return;
          plot(x, y, LEVELS[1] * 0.45 + e.energy * LEVELS[2] * 0.6);
        });
      }

      // Nodes: dim at rest, bright for a moment after firing
      for (const n of nodes) plot(n.x, n.y, LEVELS[2] * 0.7 + n.energy * (LEVELS[4] - LEVELS[2] * 0.7));

      // Signals: one bright pixel with a two-cell fading tail
      for (const s of signals) {
        const e = edges[s.edge];
        const path: [number, number][] = s.forward ? [[nodes[e.a].x, nodes[e.a].y], ...e.cells, [nodes[e.b].x, nodes[e.b].y]] : [[nodes[e.b].x, nodes[e.b].y], ...[...e.cells].reverse(), [nodes[e.a].x, nodes[e.a].y]];
        const head = Math.min(path.length - 1, Math.floor(s.p * (path.length - 1)));
        [LEVELS[4], LEVELS[3] * 0.7, LEVELS[2] * 0.6].forEach((lvl, k) => {
          const c = path[head - k];
          if (c) plot(c[0], c[1], lvl);
        });
      }
      ctx.globalAlpha = 1;
      if (visible && !reduce) raf = requestAnimationFrame(draw);
    };
    const onMove = (e: PointerEvent) => {
      const rect = canvas.getBoundingClientRect();
      if (e.clientX < rect.left || e.clientX > rect.right || e.clientY < rect.top || e.clientY > rect.bottom) {
        hovering = false;
        return;
      }
      target.x = (e.clientX - rect.left) / cell;
      target.y = (e.clientY - rect.top) / cell;
      if (!hovering && strength < 0.01) {
        smooth.x = target.x;
        smooth.y = target.y;
      }
      hovering = true;
    };
    const onLeave = () => {
      hovering = false;
    };
    const io = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      cancelAnimationFrame(raf);
      last = performance.now();
      if (visible || reduce) raf = requestAnimationFrame(draw);
    });
    const ro = new ResizeObserver(() => {
      resize();
      if (reduce) draw(performance.now());
    });
    resize();
    ro.observe(host);
    io.observe(host);
    window.addEventListener('pointermove', onMove, {
      passive: true
    });
    document.addEventListener('pointerleave', onLeave);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      io.disconnect();
      window.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerleave', onLeave);
    };
  }, [cell]);
  return <canvas ref={canvasRef} aria-hidden="true" className={`pixel-anim pointer-events-none absolute inset-0 ${className}`} data-id="element-161" data-mp="2m97e3e8xkd74" />;
}